<script setup lang="ts">
/**
 * Renders the body of a single sidebar section (title + items). Extracted from
 * BaseLayout so the same markup can be reused for both the main-flow sections
 * and the pinned-bottom sections without duplicating ~200 lines of template.
 */
import { Link, usePage } from '@inertiajs/vue3';
import { ChevronRight, Database, LifeBuoy } from '@lucide/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/index';

import type { LayoutItem, LayoutSection } from '../types/layout';
import { getSidebarComponent } from '../utils/sidebarComponents';

const props = defineProps<{
    section: LayoutSection;
    /** Current portal route prefix (e.g. '/crud', '' for root) — used by isInternalRoute. */
    routePrefix: string;
}>();

const page = usePage();
const { t: translate, te: translationExists } = useI18n();

const currentUrl = computed(() => page.url);

/**
 * Sections with the `recent-history` item template are rendered by the
 * component the owning package registered (Crud's SidebarRecentItems),
 * keeping crud-specific markup out of the Portal layer.
 */
const recentHistoryComponent = computed(() =>
    props.section.itemTemplate === 'recent-history' ? getSidebarComponent('SidebarRecentItems') : undefined,
);

function isRouteActive(route: string): boolean {
    const url = currentUrl.value;
    return url === route || url.startsWith(route + '/');
}

function isInternalRoute(route: string, item?: LayoutItem): boolean {
    if (item?.inertia) return true;
    const prefix = props.routePrefix;
    if (prefix === '') {
        return route === '/' || route.startsWith('/?');
    }
    return route === prefix || route.startsWith(prefix + '/') || route.startsWith(prefix + '?');
}

function translateLabel(item: LayoutItem, type: 'singular' | 'plural' = 'plural'): string {
    if (item.translationPrefix) {
        const key = `models.${item.translationPrefix}.${type}`;
        if (translationExists(key)) return translate(key);
    }
    if (item.label && translationExists(item.label)) return translate(item.label);
    return item.label;
}

function dispatchSidebarEvent(name?: string): void {
    if (!name) return;
    window.dispatchEvent(new CustomEvent(name));
}
</script>

<template>
    <div
        v-if="section.title && section.itemTemplate !== 'recent-history'"
        class="text-muted-foreground px-3 pb-2 text-xs font-medium uppercase tracking-wider"
    >
        {{ section.title }}
    </div>

    <!-- Recent-history section: rendered by the registered package component -->
    <template v-if="section.itemTemplate === 'recent-history'">
        <component :is="recentHistoryComponent" v-if="recentHistoryComponent" />
    </template>

    <!-- Regular static items -->
    <template v-else>
        <template v-for="item in section.items" :key="item.key">
            <!-- Item with children: link + dropdown for quick access -->
            <div v-if="item.route && item.children?.length" class="flex items-center">
                <Link
                    v-if="isInternalRoute(item.route, item)"
                    :href="item.route"
                    :class="[
                        'flex flex-1 items-center gap-3 rounded-l-lg px-3 py-2 text-sm transition-colors',
                        item.active || isRouteActive(item.route)
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    ]"
                >
                    <Database class="size-4" />
                    {{ translateLabel(item) }}
                </Link>
                <a
                    v-else
                    :href="item.route"
                    :class="[
                        'flex flex-1 items-center gap-3 rounded-l-lg px-3 py-2 text-sm transition-colors',
                        'text-muted-foreground hover:bg-muted hover:text-foreground',
                    ]"
                >
                    <Database class="size-4" />
                    {{ translateLabel(item) }}
                </a>
                <DropdownMenu>
                    <DropdownMenuTrigger
                        :class="[
                            'rounded-r-lg px-2 py-2 transition-colors',
                            item.active || isRouteActive(item.route)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        ]"
                    >
                        <ChevronRight class="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" class="w-48">
                        <DropdownMenuLabel class="text-xs">{{ translateLabel(item) }}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <template v-for="child in item.children" :key="child.key">
                            <DropdownMenuItem v-if="child.route" as-child>
                                <Link
                                    v-if="child.route && isInternalRoute(child.route, child)"
                                    :href="child.route"
                                    class="flex w-full cursor-pointer items-center gap-2"
                                >
                                    <Database class="size-3" />
                                    {{ translateLabel(child) }}
                                </Link>
                                <a
                                    v-else-if="child.route"
                                    :href="child.route"
                                    class="flex w-full cursor-pointer items-center gap-2"
                                >
                                    <Database class="size-3" />
                                    {{ translateLabel(child) }}
                                </a>
                            </DropdownMenuItem>
                        </template>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <!-- Event-bound item -->
            <button
                v-else-if="item.event"
                type="button"
                @click="dispatchSidebarEvent(item.event)"
                :class="[
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    item.variant === 'emergency'
                        ? 'bg-destructive/10 text-destructive hover:bg-destructive/15'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                ]"
            >
                <component :is="item.variant === 'emergency' ? LifeBuoy : Database" class="size-4" />
                {{ translateLabel(item) }}
                <span v-if="item.badge" class="bg-primary/20 ml-auto rounded-full px-2 py-0.5 text-xs font-medium">
                    {{ item.badge }}
                </span>
            </button>

            <!-- Simple link items -->
            <Link
                v-else-if="item.route && isInternalRoute(item.route, item)"
                :href="item.route"
                :class="[
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    item.variant === 'emergency'
                        ? (item.active || isRouteActive(item.route)
                            ? 'bg-destructive text-destructive-foreground'
                            : 'bg-destructive/10 text-destructive hover:bg-destructive/15')
                        : (item.active || isRouteActive(item.route)
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'),
                ]"
            >
                <component :is="item.variant === 'emergency' ? LifeBuoy : Database" class="size-4" />
                {{ translateLabel(item) }}
                <span v-if="item.badge" class="bg-primary/20 ml-auto rounded-full px-2 py-0.5 text-xs font-medium">
                    {{ item.badge }}
                </span>
            </Link>
            <a
                v-else-if="item.route"
                :href="item.route"
                :class="[
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    item.variant === 'emergency'
                        ? 'bg-destructive/10 text-destructive hover:bg-destructive/15'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                ]"
            >
                <component :is="item.variant === 'emergency' ? LifeBuoy : Database" class="size-4" />
                {{ translateLabel(item) }}
                <span v-if="item.badge" class="bg-primary/20 ml-auto rounded-full px-2 py-0.5 text-xs font-medium">
                    {{ item.badge }}
                </span>
            </a>
        </template>
    </template>
</template>
